import CustomButton from "components/common/Button/CustomButton";
import DateRangeWithRegister from "components/common/DateRangePicker/DateRangePickerWithRegister";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { apiPostRequest } from "helpers/Requests";
import { notifyPendingLeaveChanged } from "helpers/pendingLeaveBadge";
import {
  URL_HRM_CHECK_WORKER_LEAVE_SHIFT_OVERLAP,
  URL_SP_CHECK_WORKER_LEAVE_SHIFT_OVERLAP,
} from "store/services/URL";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import moment from "moment";

const WorkerLeaveModal = (props) => {
  const {
    openModal,
    handleClose,
    isUserGracePeriodOver,
    WorkerId,
    apiType = "sp",
    onLeaveAdded,
  } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control,
    setValue,
    trigger,
    setError,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      is_partial_leave: false,
      start_time: "",
      end_time: "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  /** When set, user has seen inline shift warning and next submit sends acknowledge_scheduled_shifts */
  const [shiftOverlapInfo, setShiftOverlapInfo] = useState(null);

  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const is_partial_leave = watch("is_partial_leave");
  const start_time = watch("start_time");
  const end_time = watch("end_time");

  useEffect(() => {
    if (startDate && endDate) {
      trigger(["startDate", "endDate"]);
    }
  }, [startDate, endDate, trigger]);

  useEffect(() => {
    if (is_partial_leave && startDate) {
      setValue("endDate", new Date(startDate));
    }
  }, [is_partial_leave,startDate,setValue]);

  // Clear time fields when partial leave is turned off
  useEffect(() => {
    if (!is_partial_leave) {
      setValue("start_time", "");
      setValue("end_time", "");
      // re-validate time fields when clearing
      trigger(["start_time", "end_time"]);
    }
  }, [is_partial_leave, setValue, trigger]);

  useEffect(() => {
    setShiftOverlapInfo(null);
  }, [startDate, endDate, is_partial_leave, start_time, end_time]);

  useEffect(() => {
    if (!openModal) {
      setShiftOverlapInfo(null);
    }
  }, [openModal]);

  const handleSubmitButton = async (data) => {
    try {
      setIsLoading(true);

      // ensure times are null when partial leave is OFF
      const startTimePayload = data.is_partial_leave
        ? data.start_time || null
        : null;
      const endTimePayload = data.is_partial_leave
        ? data.end_time || null
        : null;

      const payload = {
        worker_id: WorkerId,
        start_date: data.startDate,
        end_date: data.endDate,
        details: data.details || null,
        is_partial_leave: data.is_partial_leave || false,
        start_time: startTimePayload,
        end_time: endTimePayload,
      };

      const checkPath =
        apiType === "hrm"
          ? URL_HRM_CHECK_WORKER_LEAVE_SHIFT_OVERLAP
          : URL_SP_CHECK_WORKER_LEAVE_SHIFT_OVERLAP;

      if (!shiftOverlapInfo) {
        try {
          const checkRes = await apiPostRequest(checkPath, {
            worker_id: WorkerId,
            start_date: data.startDate,
            end_date: data.endDate,
            is_partial_leave: data.is_partial_leave || false,
            start_time: startTimePayload,
            end_time: endTimePayload,
          });
          const overlapPayload =
            checkRes?.data?.data ?? checkRes?.data ?? checkRes;
          if (overlapPayload?.has_scheduled_shifts) {
            setShiftOverlapInfo({
              message:
                overlapPayload.message ||
                "This worker has scheduled shifts during the selected period. Approved leave will release those shifts.",
              dates: overlapPayload.overlapping_shift_dates || [],
              count: overlapPayload.overlap_count ?? 0,
            });
            setIsLoading(false);
            return;
          }
        } catch (checkErr) {
          setIsLoading(false);
          const msg =
            checkErr?.response?.data?.message ||
            "Could not verify scheduled shifts. Try again.";
          toast.error(msg);
          return;
        }
      } else {
        payload.acknowledge_scheduled_shifts = true;
      }

      // Choose endpoint dynamically
      const endpoint =
        apiType === "hrm" ? "hrm/worker-leave" : "sp/worker-leave";

      const response = await apiPostRequest(endpoint, payload);

      if (response?.status === 200 || response?.status === 201) {
        setShiftOverlapInfo(null);
        reset();
        notifyPendingLeaveChanged();
        if (onLeaveAdded) onLeaveAdded(); // refresh leave list
        handleClose(false);
      }
    } catch (error) {
      const backendMsg =
        error?.response?.data?.message || "Leave Request already exists";
      // Attach backend validation under End Date
      setError("endDate", {
        type: "backend",
        message: backendMsg,
      });

      toast.error(backendMsg);
    } finally {
      setIsLoading(false);
    }
  };


  const modalFooter = () => (
    <>
      <CustomButton
        showLoading={isLoading}
        isDisabled={isLoading || isUserGracePeriodOver}
        label={shiftOverlapInfo ? "Confirm & submit leave" : "Submit"}
        clickHandler={handleSubmit(handleSubmitButton)}
      />
    </>
  );



  return (
    <ModalSkeleton
      isOpen={openModal}
      title={"Add Leave Request"}
      cssClass='w-[50%] h-auto min-w-[400px]'
      closeModal={() => handleClose(false)}
      modalFooter={modalFooter()}
    >
      <div>
        <input
          type='text'
          tabIndex={0}
          aria-hidden='true'
          ref={(el) => el && el.blur()}
          className='absolute -left-[9999px] w-px h-px opacity-0'
        />
        {shiftOverlapInfo && (
          <div
            className='mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950'
            role='alert'
          >
            <p className='font-semibold text-amber-900'>
              Scheduled shifts overlap leave request
            </p>
            {/* <p className='mt-2 leading-relaxed'>{shiftOverlapInfo.message}</p> */}
            {shiftOverlapInfo.count > 0 && (
              <p className='mt-1 text-xs text-amber-800'>
                {shiftOverlapInfo.count} shift
                {shiftOverlapInfo.count === 1 ? "" : "s"} on the roster for
                these dates.
              </p>
            )}
            {shiftOverlapInfo.dates?.length > 0 && (
              <p className='mt-2 text-xs font-medium text-amber-900'>
                Dates:{" "}
                {shiftOverlapInfo.dates
                  .map((d) => moment(d).format("DD/MM/YYYY"))
                  .join(", ")}
              </p>
            )}
            {/* <p className='mt-3 text-xs text-amber-800'>
              Review the dates above, then press{" "}
              <span className='font-semibold'>Confirm &amp; submit leave</span>{" "}
              to create this request, or change your dates to dismiss this
              notice.
            </p>
            <button
              type='button'
              className='mt-3 text-xs font-medium text-amber-900 underline hover:text-amber-950'
              onClick={() => setShiftOverlapInfo(null)}
            >
              Dismiss notice (you can edit dates and submit again)
            </button> */}
          </div>
        )}

        <div className='flex items-center justify-between'>
          <p className='text-sm text-gray-500 mb-0'>
            Please add worker leave request here
          </p>

          {/* <div className='flex items-center select-none'>
            <input
              type='checkbox'
              {...register("is_partial_leave")}
              className='sr-only'
              id='partialLeaveToggle'
            />

            <div
              onClick={() => {
                const current = watch("is_partial_leave");
                setValue("is_partial_leave", !current);
              }}
              className={`w-10 h-6 rounded-full mr-3 cursor-pointer transition-colors duration-200 
        ${watch("is_partial_leave") ? "bg-blue-500" : "bg-gray-300"}`}
            >
              <div
                className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 mt-1 ml-1
          ${watch("is_partial_leave") ? "translate-x-4" : ""}`}
              />
            </div>

            <span className='text-sm text-gray-700'>Partial Leave</span>
          </div> */}
        </div>

        {/* Start Date + Start Time (same row) */}
        <div className='mt-1 grid grid-cols-1 sm:grid-cols-2 gap-4'>
          {/* Start Date */}
          <div>
            <label className='did-floating-label'>Start Date</label>
            <Controller
              name='startDate'
              control={control}
              rules={{
                required: "Start date is required",
                validate: (value) =>
                  !value
                    ? "Start date is required"
                    : endDate && new Date(value) > new Date(endDate)
                    ? "Start date cannot be after end date"
                    : true,
              }}
              render={({ field, fieldState }) => (
                <DateRangeWithRegister
                  isSingleDate
                  field={field}
                  fieldState={fieldState}
                  placeholder='Select Start Date'
                  showCalendarIcon
                  minDate={new Date()}
                />
              )}
            />
            {errors?.startDate && (
              <span className='text-sm text-red-500'>
                {errors.startDate.message}
              </span>
            )}
          </div>
          <div>
            <label className='did-floating-label'>End Date</label>
            <Controller
              name='endDate'
              control={control}
              defaultValues={
                watch("is_partial_leave") ? watch("startDate") : ""
              }
              rules={{
                required: "End date is required",
                validate: (value) =>
                  !value
                    ? "End date is required"
                    : startDate && new Date(value) < new Date(startDate)
                    ? "End date cannot be earlier than start date"
                    : true,
              }}
              render={({ field, fieldState }) => (
                <DateRangeWithRegister
                  isSingleDate
                  field={field}
                  fieldState={fieldState}
                  placeholder='Select End Date'
                  isDisabled={watch("is_partial_leave")}
                  showCalendarIcon
                  isClearable={!watch("is_partial_leave")}
                  minDate={
                     new Date(watch("startDate"))
             
                  }
                  maxDate={
                    watch("is_partial_leave")
                      ? new Date(watch("startDate"))
                      : undefined
                  }
                />
              )}
            />
       
            {errors?.endDate && (
              <span className='text-sm text-red-500'>
                {errors.endDate.message}
              </span>
            )}
            {/* {is_partial_leave && (
          <p className='text-xs text-blue-500 mt-1 col-span-2'>
            For partial leave, the End Date can be at most one day after the
            Start Date.
          </p>
        )} */}
          </div>
        </div>
        <div className='flex items-center  gap-2 select-none mt-2'>
          <span className='text-sm text-gray-500'>Partial Leave</span>
          <input
            type='checkbox'
            {...register("is_partial_leave")}
            className='sr-only'
            id='partialLeaveToggle'
          />

          <div
            onClick={() => {
              const current = watch("is_partial_leave");
            
              setValue("is_partial_leave", !current);
    //           if (!current) {
    //            if(startDate){
    //  setValue("endDate", new Date(startDate));
    //            }
           
    //           }
            }}
            className={`w-10 h-6 rounded-full mr-3 cursor-pointer transition-colors duration-200 
        ${watch("is_partial_leave") ? "bg-blue-500" : "bg-gray-300"}`}
          >
            <div
              className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 mt-1 ml-1
          ${watch("is_partial_leave") ? "translate-x-4" : ""}`}
            />
          </div>
        </div>
        {/* End Date + End Time */}
        {watch("is_partial_leave") && (
          <div className='mt-1 grid grid-cols-1 sm:grid-cols-2 gap-4'>
            <div>
              <label className='did-floating-label'>Start Time</label>
              <input
                type='time'
                {...register("start_time", {
                  validate: (val) =>
                    !is_partial_leave || (val && val.length)
                      ? true
                      : "Start time is required",
                })}
                disabled={!is_partial_leave}
                className={`did-floating-input w-full ${
                  !is_partial_leave ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onChange={(e) => {
                  register("start_time").onChange(e);
                  trigger("end_time");
                }}
              />
              {errors?.start_time && (
                <span className='text-sm text-red-500'>
                  {errors.start_time.message}
                </span>
              )}
            </div>

            {/* End Time */}
            <div>
              <label className='did-floating-label'>End Time</label>
              <input
                type='time'
                {...register("end_time", {
                  validate: (val) => {
                    if (!is_partial_leave) return true;
                    if (!val || !val.length) return "End time is required";
                    
                    const startTime = watch("start_time");
                    if (startTime && val && val <= startTime) {
                      return "End time must be after start time";
                    }
                    return true;
                  },
                })}
                disabled={!is_partial_leave}
                className={`did-floating-input w-full ${
                  !is_partial_leave ? "opacity-50 cursor-not-allowed" : ""
                }`}
                onChange={(e) => {
                  register("end_time").onChange(e);
                  trigger("end_time");
                }}
              />
              {errors?.end_time && (
                <span className='text-sm text-red-500'>
                  {errors.end_time.message}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Details (optional) */}
        <div className='mt-1 col-span-2 sm:col-span-1'>
          <label className='did-floating-label'>
            Details <span className='text-gray-400'>(optional)</span>
          </label>
          <textarea
            className='did-floating-input'
            style={{ minHeight: "80px", resize: "vertical" }}
            placeholder='Add additional details'
            maxLength={500}
            {...register("details", {
              maxLength: {
                value: 500,
                message: "Reason must be at most 500 characters",
              },
            })}
          />
        </div>
        {errors?.details && (
          <span className='text-sm text-red-500 mt-1 block'>
            {errors.details.message}
          </span>
        )}
        <div className='text-right text-xs text-gray-500 mt-1'>
          {Math.max(0, 500 - (watch("details")?.length || 0))} characters
          remaining
        </div>
      </div>
    </ModalSkeleton>
  );
};
export default WorkerLeaveModal;
